import { map, switchMap, tap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TokenService } from 'services/token/token.service';
import { UserService } from 'services/user/user.service';
import { EntryService } from 'services/entry/entry.service';
import { validURL } from 'utils/helpers';
import { ComputedFrom, equalTo, requestCompleted, required, textRangeLength } from '@certemy/common';
import { MixpanelService } from 'services/mixpanel/mixpanel.service';
import { ROLE_CODES, ROLE_NAMES } from 'constants/role.constants';
import { PasswordValidators } from 'validators/password-validators';
import { Store } from '@ngrx/store';
import { Actions } from 'features/profiles-state/+state/profiles-state.actions';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { Observable, Subject } from 'rxjs';
import { Actions as BrandingActions } from 'features/organization-settings/platform-branding/+state/platform-branding.actions';
import { PlatformBrandingService } from 'features/organization-settings/platform-branding/platform-branding.service';
import _capitalize from 'lodash-es/capitalize';
import _get from 'lodash-es/get';
import { GlobalOrganizationsSettingService } from 'features/global-organizations-setting-state/global-organizations-setting.service';
import {
  DEFAULT_LABEL_SETTINGS,
  GlobalOrganizationsLabel,
  OrganizationsLabelType,
} from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import { LOGIN_ERRORS, LOGIN_PROVIDER, SIGN_IN_TYPES, VerifyTokenData } from '../entry.helpers';
import { ExternalLoginSettings } from 'interfaces/external-login.interface';
import { Selectors as LoginProviderSelectors } from 'services/login-provider/+state/login-provider.state';
import { LoginProviderRedirectResponse } from 'services/login-provider/login-provider.interface';
import { PROFILE_TYPE_ID } from 'components/organization-users/users/users.constants';
import { SmartFilters } from '@certemy/ui-components';

@Component({
  selector: 'cm-invite',
  templateUrl: './invited.component.html',
  styleUrls: ['./invited.component.scss'],
})
export class InvitedComponent implements OnInit, OnDestroy {
  submitted = false;
  activated = false;
  showPasswords = true;
  showExternalLogin = true;
  inviteId = '';
  emailId = '';
  readonly rolesToEnableOrganizationName = [ROLE_CODES.SUPERVISOR];
  organizationRoles: SmartFilters.FilterOption[] = [];
  ROLES_HEADING = {
    [ROLE_CODES.PROFESSIONAL]: 'Professional',
    [ROLE_CODES.ORGANIZATION_ADMIN]: 'Organization',
    [ROLE_CODES.IMPOSER]: 'Imposer',
    [ROLE_CODES.SUPERVISOR]: '{supervisorTitle}',
  };
  profileTypeId = PROFILE_TYPE_ID.INDIVIDUAL;
  readonly PROFILE_TYPE_ID = PROFILE_TYPE_ID;
  readonly SIGN_IN_TYPES = SIGN_IN_TYPES;

  profile_id: number = null;
  errorsMessages: any[] = [];

  form: UntypedFormGroup;

  isTermsAndCondAccepted = false;
  private onDestroy$ = new Subject<void>();
  roleId: ROLE_CODES;
  supervisorLabel: string;
  disabled = false;
  locations;
  locationData = null;
  organizationsLabel: GlobalOrganizationsLabel = DEFAULT_LABEL_SETTINGS;
  readonly ROLE_CODES = ROLE_CODES;
  loginType: SIGN_IN_TYPES = SIGN_IN_TYPES.DEFAULT;
  loginProvider: LOGIN_PROVIDER = LOGIN_PROVIDER.CERTEMY;

  settings$: Observable<ExternalLoginSettings> = this.store
    .select(LoginProviderSelectors.loginProviderMappedState)
    .pipe(
      map(data => ({
        ...data,
        url: window.location.href,
      })),
    );

  @ComputedFrom('roleId')
  get headlineRoleTitle() {
    return this.roleId
      ? (this.ROLES_HEADING[this.roleId] || '').replace(
          '{supervisorTitle}',
          _capitalize(this.supervisorLabel || ROLE_NAMES[ROLE_CODES.SUPERVISOR]),
        )
      : '';
  }

  constructor(
    private activeRoute: ActivatedRoute,
    private router: Router,
    private platformBrandingService: PlatformBrandingService,
    private userService: UserService,
    private tokenService: TokenService,
    private entryService: EntryService,
    private mixpanelService: MixpanelService,
    private store: Store<any>,
    private globalOrganizationsSettingService: GlobalOrganizationsSettingService,
  ) {
    const password: UntypedFormControl = new UntypedFormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.minLength(8),
        PasswordValidators.lowercaseCharacterRule(),
        PasswordValidators.uppercaseCharacterRule(),
        PasswordValidators.oneDigitRule(),
      ]),
    );
    const confirm_password: UntypedFormControl = new UntypedFormControl(
      '',
      Validators.compose([Validators.required, equalTo(password)]),
    );

    this.form = new UntypedFormGroup({
      organization: new UntypedFormControl({ value: '', disabled: true }, required),
      first_name: new UntypedFormControl('', [required, textRangeLength('First name')]),
      last_name: new UntypedFormControl('', [required, textRangeLength('Last name')]),
      email: new UntypedFormControl({ value: '', disabled: true }),
      password,
      confirm_password,
      organizationRoleId: new UntypedFormControl({ value: null, disabled: true }),
    });
  }

  ngOnInit() {
    this.inviteId = this.activeRoute.snapshot.params['hash'];

    this.userService
      .inviteConfirmation(this.inviteId)
      .pipe(
        map(res => res.result),
        tap(result => this.platformBrandingService.initPlatformBrandingOrRedirect(result)),
      )
      .subscribe(
        resData => {
          if (resData) {
            if (resData.organizationTypeId) {
              this.organizationsLabel = this.globalOrganizationsSettingService.getLabelSettingsMapFromArr(
                resData.organizationLabelSettings,
                resData.organizationTypeId,
              );
              this.ROLES_HEADING = {
                ...this.ROLES_HEADING,
                [ROLE_CODES.PROFESSIONAL]: this.organizationsLabel[OrganizationsLabelType.GENERAL_PROFESSIONAL],
                [ROLE_CODES.ORGANIZATION_ADMIN]: this.organizationsLabel[OrganizationsLabelType.GENERAL_ORG_ADMIN],
              };
            }
            const userData: any = {
              organization: resData.organization || '',
              first_name: resData.first_name || '',
              last_name: resData.last_name || '',
              email: resData.email || '',
              password: '',
              confirm_password: '',
              organizationRoleId: null,
            };
            this.roleId = resData.role_id;

            const { locations, countryId, regionId, officeId, organizationRole } = resData;
            if (this.roleId === ROLE_CODES.PROFESSIONAL) {
              if (locations && officeId) {
                this.locations = locations;
                this.locationData = {
                  countryId,
                  regionId,
                  officeId,
                };
              }
              if (organizationRole) {
                userData.organizationRoleId = organizationRole.organizationRoleId;
                this.organizationRoles = [
                  {
                    id: organizationRole.organizationRoleId,
                    name: organizationRole.organizationRoleName,
                  },
                ];
              }
            }

            this.supervisorLabel = resData.supervisorLabel;

            this.activated = resData.activated || false;
            this.emailId = resData.email || ''; // Needed for workaround for form disabled prop

            if (this.rolesToEnableOrganizationName.includes(this.roleId)) {
              this.form.get('organization').enable();
            }

            this.profile_id = resData.profile_id;

            if (this.roleId === ROLE_CODES.PROFESSIONAL && resData.profileTypeId === PROFILE_TYPE_ID.FACILITY) {
              this.profileTypeId = resData.profileTypeId;
              const firstName = this.form.get('first_name');
              const lastName = this.form.get('last_name');
              firstName.clearValidators();
              firstName.setValidators([required, textRangeLength('Name')]);
              firstName.updateValueAndValidity();
              lastName.clearValidators();
              lastName.updateValueAndValidity();
            }

            this.form.setValue(userData);

            const queryParams = this.activeRoute.snapshot.queryParams;
            if (queryParams) {
              const { accessToken, type, organizationId, error } = queryParams as LoginProviderRedirectResponse;
              if ([SIGN_IN_TYPES.ONE_LOGIN, SIGN_IN_TYPES.OKTA, SIGN_IN_TYPES.AZURE, SIGN_IN_TYPES.ENTRA, SIGN_IN_TYPES.PING].includes(type)) {
                if (error === LOGIN_ERRORS.ACCESS_DENIED) {
                  this.errorsMessages = [LOGIN_ERRORS.INCORRECT_ACCOUNT];
                } else if (accessToken) {
                  this.verifyToken(accessToken, type, Number(organizationId));
                }
              }
            }
          }
        },
        err => {
          let redirectLink = _get(err, 'body.error_details.redirect_link[0]');
          if (redirectLink && !validURL(redirectLink)) {
            redirectLink = this.userService.generateRedirectLink(redirectLink);
          }
          // console.log(`Could not get user data. Error: ${err}`);
          if (err.body && err.body.code_name) {
            switch (err.body.code_name) {
              case 'ALREADY_ACTIVATED':
                this.entryService.setMessageType({ msg: 'profile_exists', email: true, redirectLink });
                this.router.navigate(['/entry/message']);
                break;
              case 'EXPIRED':
                this.entryService.setMessageType({ msg: 'EXPIRED', email: true, redirectLink });
                this.router.navigate(['/entry/message']);
                break;
              case 'ACCOUNT_DELETED':
                this.entryService.setMessageType({ msg: 'ACCOUNT_DELETED', email: true, redirectLink });
                this.router.navigate(['/entry/message']);
                break;
              case 'INVITATION_NO_VALID':
                this.entryService.setMessageType({ msg: 'INVITATION_NO_VALID', email: true, redirectLink });
                this.router.navigate(['/entry/message']);
                break;
              default:
                this.router.navigate(['not-found']);
            }
            return;
          }
          this.router.navigate(['not-found']);
        },
      );
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  async handleCredentialResponse(response) {
    const { credential, type } = response;
    this.errorsMessages = [];
    await this.verifyToken(credential, type);
  }

  private verifyToken(token: string, type: SIGN_IN_TYPES, organizationId?: number) {
    this.tokenService.verifyUserByToken(token, type, organizationId).subscribe((result: VerifyTokenData) => {
      if (result.email === this.emailId) {
        this.loginType = type;
        this.loginProvider =
          this.loginType === SIGN_IN_TYPES.ONE_LOGIN
            ? LOGIN_PROVIDER.ONE_LOGIN
            : this.loginType === SIGN_IN_TYPES.OKTA
            ? LOGIN_PROVIDER.OKTA
            : this.loginType === SIGN_IN_TYPES.PING
            ? LOGIN_PROVIDER.PING
            : this.loginType === SIGN_IN_TYPES.ENTRA
              ? LOGIN_PROVIDER.ENTRA
            : this.loginType === SIGN_IN_TYPES.AZURE
            ? LOGIN_PROVIDER.AZURE
            : result.domain
            ? LOGIN_PROVIDER.GOOGLE_DOMAIN
            : LOGIN_PROVIDER.GOOGLE;
        this.showPasswords = false;
        this.showExternalLogin = false;
        const firstName =
          this.roleId === ROLE_CODES.PROFESSIONAL && this.profileTypeId === PROFILE_TYPE_ID.FACILITY
            ? `${result.firstName}${result.lastName ? ` ${result.lastName}` : ''}`
            : result.firstName;
        const lastName =
          this.roleId === ROLE_CODES.PROFESSIONAL && this.profileTypeId === PROFILE_TYPE_ID.FACILITY
            ? ''
            : result.lastName;

        this.form.patchValue({
          first_name: firstName,
          last_name: lastName,
          email: result.email,
          password: 'nullN_11',
          confirm_password: 'nullN_11',
        });
      } else {
        this.errorsMessages.push('Email does not match');
      }
    });
  }

  private _isFormValid(form: UntypedFormGroup): boolean {
    // TODO: refactor this validation
    if (!form.valid) {
      for (const key of Object.keys(form.controls)) {
        const curContr = form.controls[key];
        if (curContr.errors && curContr.errors['required']) {
          if (this.activated) {
            if (key !== 'confirm_password' && key !== 'password') {
              this.updateErrors('Please fill out all fields');
              return false;
            }
          } else {
            this.updateErrors('Please fill out all fields');
            return false;
          }
        }
        if (curContr.errors && ['first_name', 'last_name'].includes(key) && curContr.errors['minlength']) {
          this.updateErrors(curContr.errors['minlength']);
          return false;
        }
        if (!this.activated && curContr.errors && key === 'password') {
          this.updateErrors(
            `Password should contain at least 8 characters and consist of
              a combination of uppercase letters, lowercase letters, numbers and special characters.`,
          );
          return false;
        }
        if (!this.activated && (key === 'confirm_password' && curContr.errors && curContr.errors['equalTo'])) {
          this.updateErrors('Password does not match the confirm password');
          return false;
        }
      }
    }

    if (!this.isTermsAndCondAccepted) {
      this.updateErrors('Please accept terms and conditions');
      return false;
    }

    return true;
  }

  onSubmit(value: any, event: Event) {
    event.preventDefault();

    this.submitted = true;
    this.errorsMessages = [];

    if (!this._isFormValid(this.form)) {
      return;
    }
    this.disabled = true;

    let link;
    let role_id;
    const dataToSend = {
      ...value,
      token: this.inviteId,
      activated: this.activated,
      email: this.emailId,
      profile_id: this.profile_id,
      accept: this.isTermsAndCondAccepted,
      // Reset password and confirm_password to avoid issues when browser autocomplete fills hidden fields
      ...(this.activated ? { password: '', confirm_password: '' } : {}),
      signin_type: this.loginType,
      login_provider: this.loginProvider,
    };

    this.userService
      .confirmInvitedUser(dataToSend)
      .pipe(
        map(response => response.result),
        switchMap(data => {
          const { token, role_id: roleId, redirect_link } = data;
          link = redirect_link;
          role_id = roleId;
          this.tokenService.replaceUserToken(token);

          this.store.dispatch(new Actions.GetProfiles());
          this.store.dispatch(new BrandingActions.GetBrandingSettings());
          return requestCompleted(this.store.select(Selectors.profilesState));
        }),
      )
      .subscribe(success => {
        if (success) {
          this.mixpanelService.register({ invited: true });
          if (!link || !validURL(link)) {
            link = this.userService.generateRedirectLink(link, { role_id });
          }
          if (typeof link === 'string') {
            window.location.replace(link);
          } else {
            this.router.navigate(link.link, { queryParams: link.queryParams }).catch(() => (this.disabled = false));
          }
        } else {
          this.disabled = false;
        }
      });
  }

  updateErrors(error: string): boolean {
    if (this.errorsMessages.indexOf(error) === -1) {
      this.errorsMessages.push(error);
      return true;
    }
    return false;
  }
}
